import { useState, useEffect, useRef } from "react"
import * as THREE from "three"
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js"
import { feature } from "topojson-client"
import { latLngToVector } from "@/app/playbook/globe/primitives/globeUtils"
import {
  classifyLandmasses, extractPolygonsFromGeoJson,
  type LandmassInfo,
} from "./landmassClassification"

const WORLD_ATLAS_URL = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let topologyCache: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let topologyPromise: Promise<any> | null = null
const fetchTopology = async () => {
  if (topologyCache) return topologyCache
  if (!topologyPromise) {
    topologyPromise = fetch(WORLD_ATLAS_URL)
      .then(r => r.json())
      .then(t => { topologyCache = t; return t })
  }
  return topologyPromise
}
// The print side panel polls this before snapshotting the globe canvas: the
// first painted frames are a bare sphere until the atlas topology arrives.
export const isGlobeTopologyLoaded = () => topologyCache !== null
const COASTLINE_ALTITUDE = 0.006
const LAND_ALTITUDE = 0.003

// Unwraps a ring's longitudes so they are continuous across the antimeridian
const unwrapRingCoords = (ring: number[][]): number[][] => {
  if (ring.length === 0) return []
  const result: number[][] = [[ring[0][0], ring[0][1]]]
  let prevLng = ring[0][0]
  let offset = 0
  for (let i = 1; i < ring.length; i++) {
    const currLng = ring[i][0]
    const diff = currLng - prevLng
    if (diff > 180) offset -= 360
    else if (diff < -180) offset += 360
    result.push([currLng + offset, ring[i][1]])
    prevLng = currLng
  }
  return result
}

// Subdivides a straight line into segments that curve along the globe surface
const interpolateArc = (
  start: number[], end: number[],
  radius: number, segments: number = 3
): number[] => {
  const positions: number[] = []
  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    const lat = start[1] + (end[1] - start[1]) * t
    const lng = start[0] + (end[0] - start[0]) * t
    const point = latLngToVector(lat, lng, radius)
    positions.push(point.x, point.y, point.z)
  }
  return positions
}

// Builds a visible coastline outline from a ring of lat/lng coordinates
const buildCoastlineOutline = (
  ring: number[][], radius: number
): LineGeometry | null => {
  if (ring.length < 2) return null
  const positions: number[] = []
  for (let i = 0; i < ring.length - 1; i++) {
    const start = ring[i]
    const end = ring[i + 1]
    const latDiff = Math.abs(end[1] - start[1])
    const lngDiff = Math.abs(end[0] - start[0])
    const maxDiff = Math.max(latDiff, lngDiff)
    const segments = Math.max(1, Math.ceil(maxDiff / 5))
    const arcPositions = interpolateArc(start, end, radius, segments)
    const startIdx = i === 0 ? 0 : 3
    for (let j = startIdx; j < arcPositions.length; j++) {
      positions.push(arcPositions[j])
    }
  }
  if (ring.length > 2) {
    const start = ring[ring.length - 1]
    const end = ring[0]
    const latDiff = Math.abs(end[1] - start[1])
    const lngDiff = Math.abs(end[0] - start[0])
    const maxDiff = Math.max(latDiff, lngDiff)
    const segments = Math.max(1, Math.ceil(maxDiff / 5))
    const arcPositions = interpolateArc(start, end, radius, segments)
    for (let j = 3; j < arcPositions.length; j++) {
      positions.push(arcPositions[j])
    }
  }
  if (positions.length < 6) return null
  const geometry = new LineGeometry()
  geometry.setPositions(positions)
  return geometry
}

// Rings spanning >300° longitude are dateline-crossing artifacts, not real landmasses
const isDatelineWraparound = (ring: number[][]): boolean => {
  if (ring.length < 2) return true
  const lngs = ring.map(c => c[0])
  const lngSpan = Math.max(...lngs) - Math.min(...lngs)
  return lngSpan > 300
}

// Perpendicular distance (in degrees, on the lng/lat plane) from point p to
// the segment a→b — the Douglas-Peucker error metric.
const pointSegmentDistanceDeg = (
  p: number[], a: number[], b: number[]
): number => {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const lengthSq = dx * dx + dy * dy
  if (lengthSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1])
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lengthSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy))
}

// Douglas-Peucker ring simplification: drops points that deviate less than
// toleranceDeg from the simplified outline. Used by tiny globes (the mobile
// HUD strip) where the full 110m coastline wiggle reads as noise.
const simplifyRing = (ring: number[][], toleranceDeg: number): number[][] => {
  if (ring.length <= 4) return ring
  const keep = new Array<boolean>(ring.length).fill(false)
  keep[0] = true
  keep[ring.length - 1] = true
  const stack: Array<[number, number]> = [[0, ring.length - 1]]
  while (stack.length > 0) {
    const [start, end] = stack.pop()!
    let maxDistance = 0
    let maxIndex = -1
    for (let i = start + 1; i < end; i++) {
      const distance = pointSegmentDistanceDeg(ring[i], ring[start], ring[end])
      if (distance > maxDistance) {
        maxDistance = distance
        maxIndex = i
      }
    }
    if (maxDistance > toleranceDeg && maxIndex > 0) {
      keep[maxIndex] = true
      stack.push([start, maxIndex], [maxIndex, end])
    }
  }
  return ring.filter((_, index) => keep[index])
}

type Point2D = [number, number]

const subdivideForSphere = (
  triangles: Point2D[][], maxEdgeDeg: number
): Point2D[][] => {
  const result: Point2D[][] = []
  const stack = [...triangles]
  while (stack.length > 0) {
    const tri = stack.pop()!
    const [a, b, c] = tri
    const e01 = Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]))
    const e12 = Math.max(Math.abs(b[0] - c[0]), Math.abs(b[1] - c[1]))
    const e20 = Math.max(Math.abs(c[0] - a[0]), Math.abs(c[1] - a[1]))
    const maxEdge = Math.max(e01, e12, e20)
    if (maxEdge <= maxEdgeDeg) { result.push(tri); continue }
    let splitA: Point2D, splitB: Point2D, other: Point2D
    if (e01 >= e12 && e01 >= e20) {
      splitA = a; splitB = b; other = c
    } else if (e12 >= e20) {
      splitA = b; splitB = c; other = a
    } else {
      splitA = c; splitB = a; other = b
    }
    const mid: Point2D = [
      (splitA[0] + splitB[0]) / 2, (splitA[1] + splitB[1]) / 2
    ]
    stack.push([splitA, mid, other])
    stack.push([mid, splitB, other])
  }
  return result
}

const buildLandFillFromPolygon = (
  rings: number[][][], radius: number, simplifyToleranceDeg?: number
): number[] | null => {
  const rawExterior = unwrapRingCoords(rings[0])
  const exterior = simplifyToleranceDeg
    ? simplifyRing(rawExterior, simplifyToleranceDeg)
    : rawExterior
  if (!exterior || exterior.length < 3) return null
  if (isDatelineWraparound(exterior)) return null
  const contour = exterior.map(
    ([lng, lat]) => new THREE.Vector2(lng, lat)
  )
  const holeArrays = rings.slice(1).map(ring => {
    const unwrapped = unwrapRingCoords(ring)
    const simplified = simplifyToleranceDeg
      ? simplifyRing(unwrapped, simplifyToleranceDeg)
      : unwrapped
    return simplified.map(([lng, lat]) => new THREE.Vector2(lng, lat))
  }).filter(hole => hole.length >= 3)
  const faces = THREE.ShapeUtils.triangulateShape(
    contour, holeArrays
  )
  if (faces.length === 0) return null
  const allVertices: Point2D[] = [
    ...contour.map(v => [v.x, v.y] as Point2D),
    ...holeArrays.flatMap(
      hole => hole.map(v => [v.x, v.y] as Point2D)
    )
  ]
  const rawTriangles: Point2D[][] = faces.map(([a, b, c]) => [
    allVertices[a], allVertices[b], allVertices[c]
  ])
  const subdivided = subdivideForSphere(rawTriangles, 5)
  const landRadius = radius + LAND_ALTITUDE
  const positions: number[] = []
  for (const [a2d, b2d, c2d] of subdivided) {
    const a = latLngToVector(a2d[1], a2d[0], landRadius)
    const b = latLngToVector(b2d[1], b2d[0], landRadius)
    const c = latLngToVector(c2d[1], c2d[0], landRadius)
    const edge1 = new THREE.Vector3().subVectors(b, a)
    const edge2 = new THREE.Vector3().subVectors(c, a)
    const normal = new THREE.Vector3().crossVectors(edge1, edge2)
    const centroid = new THREE.Vector3().addVectors(a, b).add(c)
    if (normal.dot(centroid) > 0) {
      positions.push(
        a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z
      )
    } else {
      positions.push(
        a.x, a.y, a.z, c.x, c.y, c.z, b.x, b.y, b.z
      )
    }
  }
  return positions.length > 0 ? positions : null
}

const buildLandFillGeometry = (
  geoJson: any, radius: number, simplifyToleranceDeg?: number
): THREE.BufferGeometry | null => {
  const allPositions: number[] = []
  const featureList = geoJson.features ?? [geoJson]
  featureList.forEach((feat: any) => {
    const processPolygon = (rings: number[][][]) => {
      const positions = buildLandFillFromPolygon(rings, radius, simplifyToleranceDeg)
      if (positions) allPositions.push(...positions)
    }
    if (feat.geometry.type === "Polygon") {
      processPolygon(feat.geometry.coordinates)
    } else if (feat.geometry.type === "MultiPolygon") {
      feat.geometry.coordinates.forEach(
        (polygon: number[][][]) => processPolygon(polygon)
      )
    }
  })
  if (allPositions.length === 0) return null
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(allPositions, 3)
  )
  geometry.computeVertexNormals()
  return geometry
}

export type ContinentData = {
  coastlineGeometries: LineGeometry[]
  landGeometry: THREE.BufferGeometry | null
  hiddenCoastlineGeometries: LineGeometry[]
  hiddenLandGeometry: THREE.BufferGeometry | null
}

// Converts GeoJSON land features into filled land and coastline outline geometries
const buildContinentGeometries = (
  geoJson: any, radius: number, simplifyToleranceDeg?: number
): Omit<ContinentData, "hiddenCoastlineGeometries" | "hiddenLandGeometry"> => {
  const coastlineGeometries: LineGeometry[] = []
  const coastlineRadius = radius + COASTLINE_ALTITUDE
  const featureList = geoJson.features ?? [geoJson]
  featureList.forEach((feat: any) => {
    const buildCoastline = (ring: number[][]) => {
      const unwrapped = unwrapRingCoords(ring)
      if (isDatelineWraparound(unwrapped)) return
      const simplified = simplifyToleranceDeg
        ? simplifyRing(unwrapped, simplifyToleranceDeg)
        : unwrapped
      const lineGeo = buildCoastlineOutline(simplified, coastlineRadius)
      if (lineGeo) coastlineGeometries.push(lineGeo)
    }
    if (feat.geometry.type === "Polygon") {
      buildCoastline(feat.geometry.coordinates[0])
    } else if (feat.geometry.type === "MultiPolygon") {
      feat.geometry.coordinates.forEach(
        (polygon: number[][][]) => buildCoastline(polygon[0])
      )
    }
  })
  const landGeometry = buildLandFillGeometry(geoJson, radius, simplifyToleranceDeg)
  return { coastlineGeometries, landGeometry }
}

const buildGeoJsonFromPolygons = (polygons: number[][][][]) => ({
  type: "Feature",
  geometry: {
    type: "MultiPolygon",
    coordinates: polygons,
  }
})

export const useContinents = (
  radius: number, hiddenLandmasses?: string[], simplifyToleranceDeg?: number
): ContinentData | null => {
  const [data, setData] = useState<ContinentData | null>(null)
  const prevDataRef = useRef<ContinentData | null>(null)
  const hiddenKey = JSON.stringify(hiddenLandmasses ?? [])

  useEffect(() => {
    let disposed = false
    const load = async () => {
      try {
        const topology = await fetchTopology()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const geoJson = feature(topology, topology.objects.land) as any
        const hidden = new Set(hiddenLandmasses ?? [])
        const allPolygons = extractPolygonsFromGeoJson(geoJson)
        const landmasses = classifyLandmasses(geoJson)
        const hiddenIndices = new Set<number>()
        for (const landmass of landmasses) {
          if (!hidden.has(landmass.id)) continue
          const polygonIndices = landmass.polygonIndices
          for (const polygonIndex of polygonIndices) hiddenIndices.add(polygonIndex)
        }
        const visiblePolygons = allPolygons.filter(
          (_, index) => !hiddenIndices.has(index)
        )
        const hiddenPolygons = allPolygons.filter(
          (_, index) => hiddenIndices.has(index)
        )
        const visibleData = buildContinentGeometries(
          hiddenIndices.size > 0 ? buildGeoJsonFromPolygons(visiblePolygons) : geoJson,
          radius,
          simplifyToleranceDeg
        )
        const hiddenData = hiddenPolygons.length > 0
          ? buildContinentGeometries(
            buildGeoJsonFromPolygons(hiddenPolygons), radius, simplifyToleranceDeg
          )
          : {
            coastlineGeometries: [],
            landGeometry: null,
          }
        const continentData: ContinentData = {
          ...visibleData,
          hiddenCoastlineGeometries: hiddenData.coastlineGeometries,
          hiddenLandGeometry: hiddenData.landGeometry,
        }
        if (!disposed) {
          if (prevDataRef.current) {
            prevDataRef.current.coastlineGeometries.forEach(g => g.dispose())
            if (prevDataRef.current.landGeometry) {
              prevDataRef.current.landGeometry.dispose()
            }
            prevDataRef.current.hiddenCoastlineGeometries.forEach(g => g.dispose())
            if (prevDataRef.current.hiddenLandGeometry) {
              prevDataRef.current.hiddenLandGeometry.dispose()
            }
          }
          prevDataRef.current = continentData
          setData(continentData)
        } else {
          continentData.coastlineGeometries.forEach(g => g.dispose())
          if (continentData.landGeometry) continentData.landGeometry.dispose()
          continentData.hiddenCoastlineGeometries.forEach(g => g.dispose())
          if (continentData.hiddenLandGeometry) continentData.hiddenLandGeometry.dispose()
        }
      } catch (err) {
        console.error("Failed to load continent data:", err)
      }
    }
    load()
    return () => {
      disposed = true
      if (prevDataRef.current) {
        prevDataRef.current.coastlineGeometries.forEach(g => g.dispose())
        if (prevDataRef.current.landGeometry) {
          prevDataRef.current.landGeometry.dispose()
        }
        prevDataRef.current.hiddenCoastlineGeometries.forEach(g => g.dispose())
        if (prevDataRef.current.hiddenLandGeometry) {
          prevDataRef.current.hiddenLandGeometry.dispose()
        }
        prevDataRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, hiddenKey, simplifyToleranceDeg])

  return data
}

export const useLandmassList = (): LandmassInfo[] => {
  const [landmasses, setLandmasses] = useState<LandmassInfo[]>([])
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const topology = await fetchTopology()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const geoJson = feature(topology, topology.objects.land) as any
        if (!cancelled) setLandmasses(classifyLandmasses(geoJson))
      } catch (err) {
        console.error("Failed to load landmass list:", err)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])
  return landmasses
}
